import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { crypto as cv, type dto } from '@photo-vault/shared';
import { Button, Card, Icon, Input, PageHeader } from '../../ui/index.js';
import { useAuthSession } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { rememberAlbumKey } from './albumKeys.js';

interface DecryptedAlbum {
  album: dto.Album;
  name: string;
}

export function AlbumsListPage() {
  const session = useAuthSession();
  const { metadata } = useInfra();
  const [items, setItems] = useState<DecryptedAlbum[] | 'loading' | { error: string }>('loading');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  async function reload() {
    setItems('loading');
    try {
      const { albums } = await metadata.listAlbums(session.jwt);
      const roots = albums.filter((a) => a.parentId === null);
      const decrypted = await Promise.all(
        roots.map(async (album) => {
          if (!album.wrappedAlbumKey) {
            return { album, name: '(senza chiave)' };
          }
          try {
            const albumKey = await cv.unwrapAlbumKey(album.wrappedAlbumKey, session.rsaPriv);
            rememberAlbumKey(album.rootAlbumId, albumKey);
            const name = await cv.decryptText(album.encryptedName, albumKey);
            return { album, name };
          } catch {
            return { album, name: '(decrittura fallita)' };
          }
        }),
      );
      decrypted.sort((a, b) => a.album.createdAt.localeCompare(b.album.createdAt));
      setItems(decrypted);
    } catch (err) {
      setItems({ error: err instanceof Error ? err.message : 'errore' });
    }
  }

  useEffect(() => {
    void reload();
  }, [session.jwt]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setCreateError(null);
    const name = newName.trim();
    if (!name) {
      setCreateError('inserisci un nome');
      return;
    }
    setCreating(true);
    try {
      const albumKey = await cv.generateAlbumKey();
      const wrappedAlbumKey = await cv.wrapAlbumKeyFor(albumKey, session.rsaPubB64);
      const encryptedName = await cv.encryptText(name, albumKey);
      const created = await metadata.createRootAlbum(
        { encryptedName, wrappedAlbumKey },
        session.jwt,
      );
      rememberAlbumKey(created.rootAlbumId, albumKey);
      setNewName('');
      await reload();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'errore');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow="album"
        title={
          <>
            I tuoi <span className="accent-text">album</span>
          </>
        }
        description="Ogni album radice ha la sua chiave AES-256 generata sul device e wrappata con la tua chiave pubblica. I sotto-album ereditano la stessa chiave."
      />

      <Card className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan/10 text-cyan">
            <Icon name="create_new_folder" className="text-xl" />
          </span>
          <h3 className="font-display text-base font-semibold">Nuovo album radice</h3>
        </div>
        <form onSubmit={handleCreate} className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Input
              label="nome"
              placeholder="es. Vacanze 2026"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              {...(createError ? { error: createError } : {})}
              disabled={creating}
            />
          </div>
          <Button type="submit" loading={creating} leftIcon={<Icon name="add" />}>
            Crea
          </Button>
        </form>
      </Card>

      {items === 'loading' ? (
        <p className="text-sm text-on-surface-variant">Carico…</p>
      ) : 'error' in items ? (
        <p className="text-sm text-error">Errore: {items.error}</p>
      ) : items.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 py-12 text-center">
          <Icon name="inbox" className="text-4xl text-on-surface-variant" />
          <p className="text-sm text-on-surface-variant">
            Nessun album ancora. Creane uno per iniziare.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {items.map(({ album, name }) => (
            <AlbumCard key={album.id} album={album} name={name} />
          ))}
        </div>
      )}
    </div>
  );
}

function AlbumCard({ album, name }: { album: dto.Album; name: string }) {
  return (
    <Link to={`/albums/${album.id}`} className="group block">
      <Card className="flex h-full flex-col gap-4 transition-all hover:border-on-surface-variant/40 hover:shadow-elevated">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan/10 text-cyan">
            <Icon name="folder" className="text-xl" />
          </span>
          <h4 className="flex-1 truncate font-display text-base font-semibold">{name}</h4>
          <Icon
            name="arrow_forward"
            className="text-base text-on-surface-variant transition-transform group-hover:translate-x-0.5"
          />
        </div>
        <p className="text-xs text-on-surface-variant">
          {new Date(album.createdAt).toLocaleDateString('it-IT', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}
        </p>
      </Card>
    </Link>
  );
}
